import { useState, type ReactNode, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useTenantIdentity } from '../../shell/useTenantIdentity.js';
import { createRole, ROLE_CATEGORIES, type RoleCategory } from '../../api/rolesApi.js';
import { DateInput, validateDateSubmission } from '../../../../shared/components/DateInput.js';

const ROLE_CODE_RE = /^[A-Z][A-Z0-9_]{1,63}$/;

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function RoleCreatePage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const identity = useTenantIdentity(token);
  const nav = useNavigate();
  const [roleCode, setRoleCode] = useState('');
  const [roleName, setRoleName] = useState('');
  const [roleCategoryCode, setRoleCategoryCode] = useState<RoleCategory | ''>('');
  const [roleDescription, setRoleDescription] = useState('');
  const [effectiveFromDate, setEffectiveFromDate] = useState(today());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const code = roleCode.trim().toUpperCase();
    if (!ROLE_CODE_RE.test(code)) {
      setError(t('roles.create.err.code'));
      return;
    }
    if (!roleName.trim()) {
      setError(t('roles.create.err.name'));
      return;
    }
    if (!roleCategoryCode) {
      setError(t('roles.create.err.category'));
      return;
    }
    if (!effectiveFromDate) {
      setError(t('roles.create.err.effectiveFrom'));
      return;
    }
    setBusy(true);
    try {
      const created = await createRole(
        {
          roleCode: code,
          roleName: roleName.trim(),
          roleCategoryCode,
          roleDescription: roleDescription.trim() || undefined,
          effectiveFromDate,
        },
        token,
      );
      nav(`/admin/roles/${created.roleId}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main aria-label="role-create">
      <h1>{t('roles.create.title')}</h1>
      {identity && !identity.isPlatformAdmin ? (
        <p role="status" data-testid="role-create-blocked">
          {t('roles.create.platformOnly')}
        </p>
      ) : (
        <p>{t('roles.create.everyTenant')}</p>
      )}
      <form onSubmit={onSubmit} aria-label="role-create-form" noValidate onSubmitCapture={validateDateSubmission}>
        <label htmlFor="r-code">{t('roles.create.code')}</label>
        <input
          id="r-code"
          value={roleCode}
          onChange={(e) => setRoleCode(e.target.value)}
          autoComplete="off"
        />

        <label htmlFor="r-name">{t('roles.create.name')}</label>
        <input id="r-name" value={roleName} onChange={(e) => setRoleName(e.target.value)} />

        <label htmlFor="r-category">{t('roles.create.category')}</label>
        <select
          id="r-category"
          value={roleCategoryCode}
          onChange={(e) => setRoleCategoryCode(e.target.value as RoleCategory | '')}
        >
          <option value="">{t('roles.create.categoryPlaceholder')}</option>
          {ROLE_CATEGORIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <label htmlFor="r-desc">{t('roles.create.description')}</label>
        <input
          id="r-desc"
          value={roleDescription}
          onChange={(e) => setRoleDescription(e.target.value)}
        />

        <label htmlFor="r-eff">{t('roles.create.effectiveFrom')}</label>
        <DateInput
          id="r-eff"
          type="date"
          value={effectiveFromDate}
          onValueChange={(nextValue) => setEffectiveFromDate(nextValue)}
        />

        {error ? <p role="alert">{error}</p> : null}
        <button type="submit" disabled={busy}>{t('roles.create.submit')}</button>
      </form>
    </main>
  );
}
